// Timezone utility functions for consistent datetime handling

/**
 * Convert UTC ISO string to local datetime-local format (YYYY-MM-DDTHH:mm)
 * Used for datetime-local inputs which expect local timezone
 */
export function utcToLocalInput(utcTimestamp: string): string {
  // Convert SQLite format to ISO format if needed
  let isoString = utcTimestamp;
  if (!utcTimestamp.includes('T')) {
    // SQLite format: "2025-06-13 02:05:46" -> "2025-06-13T02:05:46.000Z"
    isoString = utcTimestamp.replace(' ', 'T') + (utcTimestamp.includes('.') ? '' : '.000') + 'Z';
  }
  
  const date = new Date(isoString);
  // Get local time and format for datetime-local input
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Convert local datetime-local format to UTC ISO string
 * Used when saving datetime-local input values to database
 */
export function localInputToUtc(localInputValue: string): string {
  const date = new Date(localInputValue);
  return date.toISOString();
}

/**
 * Get the current date in YYYY-MM-DD format in local timezone
 */
export function getCurrentLocalDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Convert a local date (YYYY-MM-DD) to UTC date range for backend filtering
 * Returns start and end of day in UTC for the given local date
 */
export function localDateToUtcRange(localDate: string): { startUtc: string; endUtc: string } {
  // Create start of day in local timezone
  const startOfDay = new Date(localDate + 'T00:00:00');
  // Create end of day in local timezone  
  const endOfDay = new Date(localDate + 'T23:59:59');
  
  return {
    startUtc: startOfDay.toISOString(),
    endUtc: endOfDay.toISOString()
  };
}

/**
 * Format a UTC timestamp for display in local timezone
 * Handles both ISO format (2025-06-13T00:36:42.000Z) and SQLite format (2025-06-13 00:36:42)
 * Returns in format: "3:49 PM" 
 */
export function formatTimeLocal(utcTimestamp: string): string {
  // Convert SQLite format to ISO format if needed
  let isoString = utcTimestamp;
  if (!utcTimestamp.includes('T')) {
    // SQLite format: "2025-06-13 00:36:42" -> "2025-06-13T00:36:42.000Z"
    isoString = utcTimestamp.replace(' ', 'T') + (utcTimestamp.includes('.') ? '' : '.000') + 'Z';
  }
  
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format a UTC ISO string for display in local timezone  
 * Returns in format: "Dec 15, 2023 3:49 PM"
 */
export function formatDateTimeLocal(utcIsoString: string): string {
  const date = new Date(utcIsoString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}